import {
  useIonRouter,
  useIonViewWillEnter,
  useIonViewWillLeave,
} from "@ionic/react";
import { chevronForward, stopwatchOutline } from "ionicons/icons";
import { FC, ReactElement, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import Card from "../../components/atoms/Card";
import Info from "../../components/atoms/ErrorComponent";
import Icon from "../../components/atoms/Icon";
import ResonanceLoader from "../../components/atoms/ResonanceLoader/ResonanceLoader";
import SmallButton from "../../components/atoms/smallButton";
import { useConfig } from "../../contexts/ConfigContext";
import { ComponentType } from "../../types/Questionnaire";
import dayjs from "dayjs";
import { useResonanceLayout } from "../../components/organisms/ResonanceContext/ResonanceLayoutContext";
import ResonanceLayout from "../../components/organisms/ResonanceContext/ResonanceLayout";
import { useResonanceData } from "../../components/organisms/ResonanceContext/ResonanceDataContext";

const SequenceComponents: FC = (): ReactElement => {
  const history = useHistory();
  const { study_slug } = useParams<{ study_slug: string }>();

  const {
    t,
    i18n: { language },
  } = useTranslation();
  const ionRouter = useIonRouter();
  const { setResonanceLayout } = useResonanceLayout();

  useIonViewWillEnter(() => {
    setResonanceLayout({
      disableHeader: false,
      disableNav: false,
      backgroundColor: "white",
      title: "Questionnaires",
      welcomePage: false,
    });
  });

  const { sequences, loading, error } = useResonanceData();

  const onComponentClick = (sequence_id: number, component_id: number) => {
    console.log(
      "[onComponentClick] navigating to sequence:",
      sequence_id,
      "component:",
      component_id
    );
    ionRouter.push(
      `/${study_slug}/tabs/questionnaires/sequences/${sequence_id}/components/${component_id}`,
      "forward",
      "push"
    );
  };

  const questionnaires = sequences
    .filter((seq) => seq.status !== "DONE") // ignore finished sequences
    .flatMap((seq) =>
      seq.components
        .filter((c) => c.questionnaire_id) // keep only questionnaire components
        .map((c) => ({
          ...c,
          sequence_id: seq.id, // keep link to parent sequence
          sequenceLabel: seq.label,
          sequenceStatus: seq.status,
        }))
    )
    .filter((q) => !q.is_done) // keep only unfinished questionnaires
    .sort(
      (a, b) => new Date(a.deadline).getTime() - new Date(b.deadline).getTime()
    );

  const isInitialLoading = loading && questionnaires == null; // null or undefined
  const isEmpty = !loading && (questionnaires?.length ?? 0) === 0;

  if (error) {
    return (
      <Info
        title={t("common_server_error_title")}
        description={t("common_server_error_desc")}
        actionText={t("common_go_back")}
      />
    );
  }

  return (
    <ResonanceLayout isConnected>
      <>
      {isInitialLoading && <ResonanceLoader />}

      {!isInitialLoading && isEmpty && (
        <EmptySequencesMessage>
          {t("sequence_empty_list")}
        </EmptySequencesMessage>
      )}

      {!isInitialLoading && !isEmpty && (
        <>
          {(questionnaires ?? []).map((q) => {
            const Renderer = q.questionnaire_id
              ? QuestionnaireGamified
              : ContentGamified;
            return (
              <Renderer
                key={q.id}
                questionnaire={q}
                onComponentClick={onComponentClick}
              />
            );
          })}
          <Blank />
        </>
      )}
      </>
    </ResonanceLayout>
  );
};

interface PropsGamified {
  questionnaire: ComponentType;
  onComponentClick(s: number, c: number): void;
}

const QuestionnaireGamified: FC<PropsGamified> = ({
  questionnaire: questionnaire,
  onComponentClick,
}) => {
  const { study } = useConfig();
  const {
    t,
    i18n: { language },
  } = useTranslation();

  return (
    <QuestionnaireCard
      onClick={() => {
        console.log(
          "[QuestionnaireGamified] Card clicked:",
          questionnaire.sequence_id,
          questionnaire.id
        );
        onComponentClick(questionnaire.sequence_id, questionnaire.id);
      }}
    >
      <QuestionnaireTitle>
        {questionnaire.questionnaire_title}
      </QuestionnaireTitle>
      {/* Conditionally render the image only if the study is gamified */}
      {/*study.is_gamified && <QuestionnaireImage src={imageSrc} />*/}
      <QuestionnaireIcons>
        <QuestionnaireIcon src="/assets/icon/doc.svg" />
        <QuestionnaireIconLabel>
          {questionnaire.questions_count} {t("questionnaire_questionnaire")}
        </QuestionnaireIconLabel>
        <QuestionnaireIcon src={stopwatchOutline} />
        <QuestionnaireIconLabel>
          {questionnaire.avg_amount_time_to_answer} {t("questionnaire_minutes")}
        </QuestionnaireIconLabel>
      </QuestionnaireIcons>

      {/* Conditionally render points only if the study is gamified */}
      {study.is_gamified && (
        <>
          <br />
          <QuestionnaireIcons>
            <QuestionnaireIcon src="/assets/icon/gift.svg" />
            <QuestionnaireIconLabel>
              {questionnaire.points} {t("questionnaire_point")}
            </QuestionnaireIconLabel>
          </QuestionnaireIcons>
        </>
      )}

      <QuestionnaireMeta>
        <div>
          Deadline: {dayjs(questionnaire.deadline).format("DD/MM/YYYY")}
        </div>
        <QuestionnaireButton>
          {questionnaire.answered_questions_count > 0
            ? String(t("questionnaire_continue"))
            : String(t("questionnaire_start"))}
        </QuestionnaireButton>
      </QuestionnaireMeta>

      <ProgressContainer>
        {/* <ProgressLabel>
    {Math.round((questionnaire.answered_questions_count / questionnaire.questions_count) * 100)}%
  </ProgressLabel> */}
        <ProgressBarWrapper>
          <ProgressFill
            $width={
              (questionnaire.answered_questions_count /
                questionnaire.questions_count) *
              100
            }
          />
        </ProgressBarWrapper>
      </ProgressContainer>
    </QuestionnaireCard>
  );
};

const ContentGamified: FC<PropsGamified> = ({
  questionnaire: questionnaire,
  onComponentClick,
}) => {
  return (
    <ComponentCard
      as="div"
      onClick={() => {
        console.log(
          "[ContentGamified] Card clicked:",
          questionnaire.content_id,
          questionnaire.questionnaire_id
        );
        onComponentClick(
          questionnaire.content_id,
          questionnaire.questionnaire_id
        );
      }}
    >
      <div>
        <ComponentTitle>{questionnaire.content_label}</ComponentTitle>
      </div>

      <ComponentIcon icon={chevronForward} />
    </ComponentCard>
  );
};

export default SequenceComponents;

const EmptySequencesMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 35px;
  height: 100%;
  text-align: center;
  padding: 20px;
`;

const ComponentCard = styled(Card)`
  padding: 20px 10px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const QuestionnaireCard = styled(Card)<React.HTMLAttributes<HTMLDivElement>>`
  padding: 10px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
`;

const Blank = styled.div`
  width: 100%;
  height: 50px;
`;

const ProgressContainer = styled.div`
  width: 100%;
  margin: 10px 0;
`;

const ProgressBarWrapper = styled.div`
  background-color: #e6e6e6;
  border-radius: 10px;
  height: 10px;
  overflow: hidden;
`;

const ProgressFill = styled.div<{ $width?: number }>`
  background: linear-gradient(
    270deg,
    #2db6b4 2.69%,
    #2db6b4 2.7%,
    #abf0ef 105.65%
  );
  height: 100%;
  border-radius: 10px;
  width: ${(props) => props.$width}%;
  transition: width 0.4s ease-in-out;
`;

const QuestionnaireTitle = styled.div`
  font-size: 22px;
  font-weight: 600;
  margin-bottom: 30px;
`;

const QuestionnaireButton = styled(SmallButton)`
  background-color: ${(p) => p.theme.secondary.main};
  color: ${(p) => p.theme.secondary.contrast};
  font-weight: 600;
`;

const QuestionnaireIcons = styled.div`
  display: flex;
  justify-content: row;
  align-items: center;
  width: 100%;
`;

const QuestionnaireIconLabel = styled.span`
  margin-left: 5px;
  margin-right: 10px;
`;
const QuestionnaireIcon = styled(Icon)`
  color: ${(p) => p.theme.primary.main};
  font-size: 25px;
  padding: 0;
  margin: 0;
`;

const QuestionnaireMeta = styled.div`
  display: flex;
  justify-content: row;
  width: 100%;
  justify-content: space-between;
  margin: 10px 0 0px;
`;

const ComponentTitle = styled.div`
  font-size: 18px;
  font-weight: bold;
`;

const ComponentIcon = styled(Icon)`
  color: #929292;
  font-size: 20px;
`;
